import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import { ArrowLeft, ArrowRight, Bell, Check, ChevronDown, Copy, Dumbbell, ExternalLink, Mic, Play, Square, Timer, TrendingUp } from 'lucide-react';

import alexaPhoto from '../../assets/landing/alexa-home-training.jpg';
import { GET_ME, GET_WORKOUTS } from '../graphql/queries';
import { trackEvent, trackPageView } from '../utils/gt';
import { lockedWorkoutIds } from '../utils/workoutLock';
import { prefersReducedMotion } from '../utils/motion';
import { buildTalk, canTellOwnSession } from '../utils/alexaTalk';
import { AlexaTalkSheet } from '../components/alexa/AlexaTalkSheet';
import '../styles/pages/divisao-alexa.css';

const STEPS = ['email', 'skill', 'workouts', 'voice'];
// The steps marked done, per device
const STORAGE_KEY = 'mt-alexa-steps';

// The voice commands, in the order a session uses them. They're the pt-BR skill's (alexa-meu-treino:
// interactionModels/custom/pt-BR.json and lambda/index.js); the English skill's aren't in that repo, so the English
// site keeps to the start phrase it already taught
const COMMANDS = [
  { key: 'start', Icon: Mic },
  { key: 'pick', Icon: Dumbbell },
  { key: 'rest', Icon: Timer },
  { key: 'end', Icon: Square },
  { key: 'resume', Icon: Play },
];

const loadDone = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return Array.isArray(saved) ? STEPS.filter((id) => saved.includes(id)) : [];
  } catch {
    return [];
  }
};

const saveDone = (ids) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Private mode or storage off: the marks last until the page closes
  }
};

/**
 * Connect Alexa (owner's pick 29/10, "A · Guia"): the four setup steps as a checklist, one open at a time, each
 * with what to do right there and a button to check it off (the progress stays on the device). Beside it (below on
 * phones) the photo and what to say during a workout, with "Ver na prática": the session told as a chat
 */
export const Tutorial = () => {
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const ui = (key, options) => t(`pages.Tutorial.ui.${key}`, options);
  const talkCopy = (key, options) => t(`pages.Tutorial.talk.${key}`, options);
  const { data: dataMe } = useQuery(GET_ME);
  const { data: dataWorkouts } = useQuery(GET_WORKOUTS);
  const me = dataMe?.me;
  const email = me?.email;
  // The commands and the chat are the pt-BR skill's
  const hasVoiceGuide = language === 'pt';

  const [done, setDone] = useState(loadDone);
  const [open, setOpen] = useState(() => STEPS.find((id) => !loadDone().includes(id)) ?? null);
  const [isTalkOpen, setIsTalkOpen] = useState(false);
  const headRefs = useRef({});

  useEffect(() => {
    trackPageView('sync', `${t('appUrl')}/sync`, '/sync');
  }, [t]);

  // The workouts the skill reads: the account's, by letter, without the ones kept locked after a Pro plan ended
  const voiceWorkouts = useMemo(() => {
    const workouts = dataWorkouts?.workouts?.data;
    if (!workouts) return null;
    const locked = lockedWorkoutIds(workouts, me);
    return workouts
      .filter((workout) => !locked.has(workout.id))
      .sort((a, b) => (a.attributes?.name || '').localeCompare(b.attributes?.name || ''));
  }, [dataWorkouts, me]);

  // Back to wherever the user came from; straight links land on the dashboard
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/dashboard'));

  const setDoneAndSave = (next) => {
    setDone(next);
    saveDone(next);
  };

  const toggleDone = (id) => {
    const isDone = done.includes(id);
    trackEvent(isDone ? 'sync_step_undone' : 'sync_step_done', `Sync - Step ${isDone ? 'Undone' : 'Done'}: ${id}`, 'sync', true);
    setDoneAndSave(isDone ? done.filter((step) => step !== id) : STEPS.filter((step) => step === id || done.includes(step)));
  };

  // "Feito": checks the step off and opens the next one still to do (none once all four are)
  const finishStep = (id) => {
    const next = STEPS.filter((step) => step === id || done.includes(step));
    if (!done.includes(id)) {
      trackEvent('sync_step_done', `Sync - Step Done: ${id}`, 'sync', true);
      setDoneAndSave(next);
    }
    const after = STEPS.slice(STEPS.indexOf(id) + 1);
    goTo(after.find((step) => !next.includes(step)) ?? STEPS.find((step) => !next.includes(step)) ?? null);
  };

  const goTo = (id) => {
    setOpen(id);
    if (!id) return;
    // The next step's heading takes the focus and comes into view once it has rendered open
    requestAnimationFrame(() => {
      const head = headRefs.current[id];
      head?.focus({ preventScroll: true });
      head?.scrollIntoView?.({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    });
  };

  const copyEmail = async () => {
    trackEvent('sync_copy_email', 'Sync - Copy Email', 'sync', true);
    try {
      await navigator.clipboard.writeText(email);
      toast.success(ui('copied'), { toastId: 'sync-copy', autoClose: 2000 });
    } catch {
      toast.error(ui('copyFailed'), { toastId: 'sync-copy' });
    }
  };

  const openTalk = () => {
    trackEvent('sync_demo_open', 'Sync - Demo Open', 'sync', true);
    setIsTalkOpen(true);
  };

  const firstLetter = voiceWorkouts?.[0]?.attributes?.name || 'A';

  // The chat tells a session of the account's first workout when the skill would ask which one to do; otherwise
  // the example's
  const talk = (() => {
    if (!hasVoiceGuide || !isTalkOpen) return null;
    const isOwn = canTellOwnSession(voiceWorkouts);
    const example = talkCopy('example', { returnObjects: true });
    const workout = isOwn
      ? { description: voiceWorkouts[0].attributes.description, exercises: voiceWorkouts[0].attributes.exercises }
      : example.workout;
    const letters = isOwn ? voiceWorkouts.map((item) => item.attributes.name) : example.letters;
    return {
      lines: buildTalk({
        copy: talkCopy,
        firstName: me?.name?.trim().split(/\s+/)[0],
        letters,
        workout,
        formatNumber: (value) => value.toLocaleString('pt-BR', { maximumFractionDigits: 2 }),
      }),
      subtitle: isOwn
        ? talkCopy('subOwn', { workout: [talkCopy('workout', { letter: letters[0] }), workout.description].filter(Boolean).join(', ') })
        : talkCopy('subExample'),
    };
  })();

  const photo = <img className="mt-ax-photo" src={alexaPhoto} alt={ui('photoAlt')} />;

  const stepBody = (id) => {
    if (id === 'email')
      return (
        <>
          <p>{ui('s1Text')}</p>
          {email && (
            <div className="mt-ax-email">
              <span className="mt-ax-email__text">
                <small>{ui('s1Email')}</small>
                <b>{email}</b>
              </span>
              <button type="button" className="mt-btn mt-btn--quiet mt-ax-email__copy" onClick={copyEmail} aria-label={ui('copyLabel')}>
                <Copy aria-hidden="true" />
                <span>{ui('copy')}</span>
              </button>
            </div>
          )}
        </>
      );
    if (id === 'skill')
      return (
        <>
          <p>{ui('s2Text')}</p>
          <p className="mt-ax-extra">
            <Bell aria-hidden="true" />
            <span>{ui('s2Permissions')}</span>
          </p>
          <div className="mt-ax-step__actions">
            <a
              className="mt-btn mt-btn--quiet mt-ax-step__btn"
              href={t('alexaUrl')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('sync_enable_skill', 'Sync - Enable Skill', 'sync', true)}
            >
              {ui('s2Cta')}
              <ExternalLink aria-hidden="true" />
              <span className="mt-sr-only"> {ui('newTab')}</span>
            </a>
          </div>
        </>
      );
    if (id === 'workouts')
      return (
        <>
          <p>{ui('s3Text')}</p>
          {voiceWorkouts && (
            <p className="mt-ax-workouts">
              {voiceWorkouts.length === 0 ? (
                ui('s3None')
              ) : (
                <>
                  {ui('s3Have', { count: voiceWorkouts.length })}{' '}
                  {voiceWorkouts.map((workout, index) => (
                    <span key={workout.id} className="mt-ax-workouts__item">
                      <b>{workout.attributes.name}</b>
                      {workout.attributes.description}
                      {index < voiceWorkouts.length - 1 ? ' · ' : ''}
                    </span>
                  ))}
                </>
              )}
            </p>
          )}
          <Link className="mt-ax-link" to="/workouts">
            {ui('s3Cta')}
            <ArrowRight aria-hidden="true" />
          </Link>
        </>
      );
    return (
      <>
        <p>{ui('s4Text')}</p>
        <p className="mt-ax-quote">{ui('s4Quote')}</p>
        <p>{ui('s4After')}</p>
      </>
    );
  };

  const doneCount = done.length;

  return (
    <div className="mt-root mt-root--app mt-ax-page">
      <header className="mt-editor-bar mt-ax-bar">
        <button type="button" className="mt-editor-bar__back" onClick={goBack}>
          <ArrowLeft aria-hidden="true" />
          <span className="mt-editor-bar__back-label">{ui('back')}</span>
        </button>
        <span />
        <span />
      </header>

      <div className="mt-ax-layout">
        <div className="mt-ax-main">
          {!isDesktop && photo}
          <h1 className="mt-ax-title">{ui('title')}</h1>
          <p className="mt-ax-sub">{t('pages.Tutorial.subtitle')}</p>

          <div className="mt-ax-progress">
            <div
              className="mt-ax-progress__bar"
              role="progressbar"
              aria-label={ui('progressLabel')}
              aria-valuemin={0}
              aria-valuemax={STEPS.length}
              aria-valuenow={doneCount}
            >
              <i style={{ width: `${(doneCount / STEPS.length) * 100}%` }} />
            </div>
            <span>{doneCount === STEPS.length ? ui('allDone') : ui('progress', { done: doneCount, total: STEPS.length })}</span>
          </div>

          <ol className="mt-ax-steps">
            {STEPS.map((id, index) => {
              const isDone = done.includes(id);
              const isOpen = open === id;
              const n = index + 1;
              const isLast = index === STEPS.length - 1;
              return (
                <li key={id} className={`mt-ax-step${isDone ? ' is-done' : ''}${isOpen ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="mt-ax-step__mark"
                    aria-pressed={isDone}
                    aria-label={isDone ? ui('markUndone', { n }) : ui('markDone', { n })}
                    onClick={() => toggleDone(id)}
                  >
                    {isDone ? <Check aria-hidden="true" /> : n}
                  </button>
                  <h2 className="mt-ax-step__title">
                    <button
                      type="button"
                      ref={(element) => {
                        headRefs.current[id] = element;
                      }}
                      aria-expanded={isOpen}
                      aria-controls={`mt-ax-step-${id}`}
                      onClick={() => setOpen(isOpen ? null : id)}
                    >
                      <span>{ui(`s${n}Title`)}</span>
                      <ChevronDown aria-hidden="true" />
                    </button>
                  </h2>
                  {isOpen && (
                    <div className="mt-ax-step__body" id={`mt-ax-step-${id}`}>
                      {stepBody(id)}
                      {(!isDone || !isLast || hasVoiceGuide) && (
                        <div className="mt-ax-step__actions">
                          {(!isDone || !isLast) && (
                            <button
                              type="button"
                              className={`mt-btn ${isDone ? 'mt-btn--quiet' : 'mt-btn--primary'} mt-ax-step__btn`}
                              onClick={() => finishStep(id)}
                            >
                              {isLast ? <Check aria-hidden="true" /> : null}
                              {isLast ? ui('doneLast') : isDone ? ui('next') : ui('doneNext')}
                              {isLast ? null : <ArrowRight aria-hidden="true" />}
                            </button>
                          )}
                          {isLast && hasVoiceGuide && (
                            <button type="button" className="mt-text-btn mt-ax-link" onClick={openTalk}>
                              <Play aria-hidden="true" />
                              {ui('demo')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        <aside className="mt-ax-side">
          {isDesktop && photo}
          {hasVoiceGuide && (
            <section className="mt-ax-commands" aria-labelledby="mt-ax-commands-title">
              <div className="mt-ax-commands__head">
                <div>
                  <h2 id="mt-ax-commands-title" className="mt-ax-commands__title">
                    {ui('commandsTitle')}
                  </h2>
                  <p className="mt-ax-commands__sub">{ui('commandsSub')}</p>
                </div>
                <button type="button" className="mt-btn mt-btn--quiet mt-ax-commands__demo" onClick={openTalk}>
                  <Play aria-hidden="true" />
                  {ui('demo')}
                </button>
              </div>
              <ul className="mt-ax-cmds">
                {COMMANDS.map(({ key, Icon }) => (
                  <li key={key} className="mt-ax-cmd">
                    <span className={`mt-ax-cmd__icon${key === 'pick' ? ' is-dumbbell' : ''}`} aria-hidden="true">
                      <Icon />
                    </span>
                    <small>{ui(`commands.${key}.when`)}</small>
                    <b>“{ui(`commands.${key}.say`, { letter: firstLetter })}”</b>
                    <span>{ui(`commands.${key}.does`)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-ax-commands__foot">
                <TrendingUp aria-hidden="true" />
                <span>{ui('commandsFoot')}</span>
              </p>
            </section>
          )}
        </aside>
      </div>

      {talk && <AlexaTalkSheet isOpen={isTalkOpen} onClose={() => setIsTalkOpen(false)} lines={talk.lines} subtitle={talk.subtitle} />}
    </div>
  );
};
